import React, { useEffect, useState } from 'react';

import { Alert, shortDate } from '../invoices/kit';
import Field from './Field';
import { ImpactTag, StatePill, TypeTag } from './Pills';
import AttachedShots from './AttachedShots';
import {
  IMPACT_ORDER,
  impactMeta,
  nextStates,
  requiresDuplicateOf,
  requiresReply,
  stateMeta,
} from './lib/feedbackTypes';
import { screenLabel } from './lib/screens';
import { MAX_OWNER, MAX_REPLY, emptyTriage, hasChanges, triagePayload, updateTriage, validateTriage } from './lib/triage';
import { audienceOf, AUDIENCE } from './lib/boardFilters';
import { updateFeedback } from '../../api_handlers/feedback';

/**
 * One item, and what a staff member can do to it.
 *
 * The status list only offers moves the state machine allows, so an
 * impossible transition is not something you can pick and then be told
 * off for. The reply box becomes required the moment the chosen status is
 * one that owes the submitter an explanation.
 */
export default function TriagePanel({ item, onSaved }) {
  const [form, setForm] = useState(() => emptyTriage(item));
  const [errors, setErrors] = useState({});
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState('');

  useEffect(() => {
    setForm(emptyTriage(item));
    setErrors({});
    setTried(false);
    setFailure('');
  }, [item]);

  if (!item) {
    return (
      <div className="fb-panel">
        <p className="fb-note">Pick an item on the left to read it and move it along.</p>
      </div>
    );
  }

  const set = (field, value) => {
    const next = updateTriage(form, field, value);
    setForm(next);
    if (tried) setErrors(validateTriage(next, item).errors);
  };

  const save = async () => {
    const check = validateTriage(form, item);
    setTried(true);
    setErrors(check.errors);
    if (!check.ok || saving) return;

    const changes = triagePayload(form, item);
    if (Object.keys(changes).length === 0) return;

    setSaving(true);
    setFailure('');
    try {
      const saved = await updateFeedback(item.id, changes);
      if (onSaved) onSaved(saved || { ...item, ...changes });
    } catch (error) {
      setFailure(error.message || 'That did not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const by = item.submitted_by || {};
  const moves = nextStates(item.state);
  const replyRequired = requiresReply(form.state);
  const dirty = hasChanges(form, item);

  return (
    <div className="fb-panel fb-triage">
      <div>
        <div className="fb-item__top">
          <StatePill state={item.state} />
          <TypeTag type={item.type} />
          <ImpactTag impact={item.impact} quiet={false} />
        </div>
        <p className="fb-item__title" style={{ marginTop: 8 }}>
          {item.title}
        </p>
      </div>

      <p className="fb-triage__body">{item.detail}</p>

      <AttachedShots shots={item.screenshots} />

      <dl className="fb-triage__meta">
        <dt className="fb-triage__key">From</dt>
        <dd className="fb-triage__val">
          {by.name || by.email || 'Unknown'}
          {audienceOf(item) === AUDIENCE.INTERNAL ? ' (internal)' : ''}
        </dd>
        <dt className="fb-triage__key">Screen</dt>
        <dd className="fb-triage__val">{screenLabel(item.screen)}</dd>
        {item.path ? (
          <>
            <dt className="fb-triage__key">Path</dt>
            <dd className="fb-triage__val">{item.path}</dd>
          </>
        ) : null}
        <dt className="fb-triage__key">Raised</dt>
        <dd className="fb-triage__val">{shortDate(item.created_at)}</dd>
        {item.user_agent ? (
          <>
            <dt className="fb-triage__key">Browser</dt>
            <dd className="fb-triage__val">
              {item.user_agent}
              {item.viewport ? ` · ${item.viewport}` : ''}
            </dd>
          </>
        ) : null}
      </dl>

      {failure ? <Alert>{failure}</Alert> : null}

      <Field id="fb-state" label="Status" error={errors.state}>
        <select
          className="iv-select"
          value={form.state}
          onChange={(e) => set('state', e.target.value)}
        >
          <option value={item.state}>{stateMeta(item.state).label} (now)</option>
          {moves.map((state) => (
            <option key={state} value={state}>
              {stateMeta(state).label}
            </option>
          ))}
        </select>
      </Field>

      <Field
        id="fb-impact"
        label="Impact"
        hint="Correct it if triage disagrees with how it was rated. There is no second priority field."
      >
        <select
          className="iv-select"
          value={form.impact}
          onChange={(e) => set('impact', e.target.value)}
        >
          {IMPACT_ORDER.map((impact) => (
            <option key={impact} value={impact}>
              {impactMeta(impact).label}
            </option>
          ))}
        </select>
      </Field>

      <Field id="fb-owner" label="Owner" error={errors.owner}>
        <input
          className={`iv-input${errors.owner ? ' iv-input--invalid' : ''}`}
          value={form.owner}
          maxLength={MAX_OWNER}
          placeholder="Who is holding this"
          onChange={(e) => set('owner', e.target.value)}
        />
      </Field>

      {requiresDuplicateOf(form.state) ? (
        <Field id="fb-dup" label="Duplicate of" error={errors.duplicate_of}>
          <input
            className={`iv-input${errors.duplicate_of ? ' iv-input--invalid' : ''}`}
            value={form.duplicate_of}
            placeholder="The id of the item that already covers this"
            onChange={(e) => set('duplicate_of', e.target.value)}
          />
        </Field>
      ) : null}

      <Field id="fb-reply" label="Reply to the person who raised it" error={errors.reply}>
        <textarea
          className={`iv-textarea${errors.reply ? ' iv-textarea--invalid' : ''}`}
          value={form.reply}
          rows={4}
          maxLength={MAX_REPLY}
          placeholder={
            replyRequired
              ? 'Required: say why this is not going ahead.'
              : 'Optional. They see this on their requests page.'
          }
          onChange={(e) => set('reply', e.target.value)}
        />
      </Field>

      <button
        type="button"
        className="iv-btn iv-btn--primary"
        onClick={save}
        disabled={saving || !dirty}
      >
        {saving ? <span className="iv-spin" /> : null}
        {saving ? 'Saving' : 'Save'}
      </button>
    </div>
  );
}
